/**
 * FieldSearchOverlay (Unit 6 Phase 1) — the records-screen search surface.
 *
 * CLONED FROM FieldColumnsModal, structurally and deliberately: full-screen own
 * chrome (not ModalShell — no header slot there for an input plus two
 * controls), and a PURE FUNCTION OF PROPS. There is no useNavigate, no
 * useLocation, and no useState for open-state anywhere in this file. The
 * seventh sentinel (tmgFieldSearchOpen) lives entirely in the host, exactly as
 * tmgFieldColumnsOpen does, so a back gesture — iOS swipe-back or Android
 * hardware back — closes this layer and nothing else.
 *
 * The ONE piece of local state is the query itself — a text input's value,
 * which is not a navigable layer and must not consume a history entry. It is
 * lifted no higher because nothing outside this overlay reads a half-typed
 * term; the host is handed the COMMITTED term on unmount instead.
 *
 * COMMIT ON UNMOUNT, one site, so every dismissal behaves identically. EVERY
 * dismissal unmounts this component — iOS swipe-back, Android hardware back,
 * and Cancel — which is why unmount is the one commit site. A close HANDLER
 * would miss both back gestures, since neither runs one, and Recently Searched
 * would sit almost always empty. This mirrors TextEditor's own unmount commit
 * (FieldRecordModal.jsx, TextEditor's commitRef): a ref onto the live value,
 * and a [] effect whose cleanup reads the ref.
 *
 * WITNESSED ON iOS ONLY. Android hardware back is a design target, untested
 * until the 9/1 walkthrough.
 *
 * ⚠️ The refs are load-bearing, not style. A [] cleanup closes over the FIRST
 * render's bindings, so reading `query` there would commit the empty string a
 * rep started with rather than what they typed. Both the value and the
 * callback are held in refs assigned on every render — not in an effect, which
 * would lag a render behind on the last keystroke before unmount.
 *
 * Opening a record from the overlay (Phase 2) does NOT unmount it — the
 * sentinel stays in location.state and the modal layers on top — so that path
 * commits nothing prematurely.
 *
 * PHASE 2 ADDS RESULTS. An empty query shows the two recents sections; a
 * non-empty one replaces them with a count bar and a result table built from
 * FieldRecordRow — the records screen's own row component, so a result row IS
 * a records row rather than a lookalike.
 *
 * ⚠️ MATCHING AND DISPLAY USE DIFFERENT LISTS, DELIBERATELY. Matching is over
 * SEARCHABLE_FIELDS (29 keys, all of them, hidden or not) plus the part-number
 * and asset-tag child arrays; the result table's columns are `visibleColumns`,
 * which the hidden-column set has already narrowed. Hiding a column tidies the
 * grid — it must never stop a rep finding a tool by that field. The visible
 * consequence is intended: a row can come back with no tinted cell, because
 * what matched is a column they hid, or a part number / asset tag, which has
 * no column at all.
 *
 * ⛔ TAPPING A RESULT DOES NOT CLOSE THE OVERLAY. It calls the host's own
 * onRowTap, which pushes the record sentinel ON TOP of tmgFieldSearchOpen
 * (Phase 1's spread fix is what makes that carry). Closing first would race
 * the push against the pop; layering means one back returns to these RESULTS,
 * not to a bare records list.
 *
 * RECENTS are the host's state, passed down. This component neither stores nor
 * caps nor de-dupes them — it renders two lists and reports intent upward
 * (onRecentTap / onRemoveSearch). One owner, so the caps cannot disagree.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { SEARCHABLE_FIELDS } from '../data/editableFields';
import { FieldRecordRow } from './FieldRecordsList';
import './FieldSearchOverlay.css';

/**
 * Which of a record's searchable fields contain the needle. Returns the SET of
 * matching keys, not a boolean, because the result table tints the cells that
 * actually matched — a rep needs to see WHY a row came back.
 *
 * `needle` is already trimmed and lowercased by the caller, and never empty:
 * folding it per record per field would be 29 × N redundant String ops.
 *
 * The value read is `record.working ? record.working[key] : undefined` — the
 * SAME guarded read as cellContent, deliberately not optional chaining and
 * deliberately not `record[key]`. Scalar values live only in `working`; the
 * top level holds record_id / tool_id / site_id / etag and the child arrays. A
 * matcher reading `record[key]` would find `tool_id` and miss all 29 real
 * fields. Empty-ish values are skipped exactly as cellContent skips them, so a
 * cell rendering '' can never be reported as a match.
 *
 * PART NUMBERS AND ASSET TAGS ARE THE OTHER HALF, and `working` never holds
 * them: buildBaseline strips the child arrays out (fieldStore.js), so they sit
 * BESIDE `working` as part_numbers / asset_tags plus an extra_* overflow. Load
 * puts slots 1-10 in the primary array and 11+ in extra_*; any local child
 * edit flattens the whole set into the primary. So both halves are read, every
 * time — the same merge as the modal's mergedChildren (FieldRecordModal.jsx),
 * inlined because that helper is not exported. An entry's text is `.value`; a
 * part number also matches on its own `.part_description`, since a rep may
 * know the part by what it is rather than by its number. `type`, `id` and
 * `local_key` are never read.
 *
 * ⚠️ THE CHILD KEYS ARE THE GROUP NAMES 'part_numbers' AND 'asset_tags' — the
 * names `dirty` and updateChildSet already use for these arrays. Nothing
 * resolves a label from this Set: the results filter reads only `.size`, and
 * FieldRecordRow tints a cell when `matched.has(col.key)`. No column carries
 * either key, so a child hit brings its row back with no tinted cell — the
 * hidden-column case again, intended. A part-description hit is filed under
 * 'part_numbers', NEVER 'part_description': that key is the TOOL's own
 * description in `working`, and filing under it would tint a PART DESCRIPTION
 * cell showing text that did not match.
 */
function matchedKeys(record, needle) {
  const hits = new Set();
  for (const f of SEARCHABLE_FIELDS) {
    const value = record.working ? record.working[f.key] : undefined;
    if (value === null || value === undefined || value === '') continue;
    if (String(value).toLowerCase().includes(needle)) hits.add(f.key);
  }
  const partNumbers = [
    ...(record.part_numbers || []),
    ...(record.extra_part_numbers || []),
  ];
  if (partNumbers.some((e) => childTextMatches(e.value, needle)
    || childTextMatches(e.part_description, needle))) {
    hits.add('part_numbers');
  }
  const assetTags = [
    ...(record.asset_tags || []),
    ...(record.extra_asset_tags || []),
  ];
  if (assetTags.some((e) => childTextMatches(e.value, needle))) {
    hits.add('asset_tags');
  }
  return hits;
}

/** The working loop's skip-and-fold, for one child-entry field. */
function childTextMatches(value, needle) {
  if (value === null || value === undefined || value === '') return false;
  return String(value).toLowerCase().includes(needle);
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export default function FieldSearchOverlay({
  records, visibleColumns, dirtyIds, onRowTap, onFlagTap,
  recentViewed, recentSearched, onRecentTap, onRemoveSearch, onCommitTerm, onClose,
}) {
  const [query, setQuery] = useState('');

  // Assigned during render, deliberately NOT in an effect: an effect runs
  // AFTER commit, so on the last keystroke before an unmount the ref would
  // still hold the previous character. Plain assignment is a render later than
  // nothing and exactly current.
  const queryRef = useRef(query);
  queryRef.current = query;

  const onCommitTermRef = useRef(onCommitTerm);
  onCommitTermRef.current = onCommitTerm;

  // THE ONE COMMIT SITE. Empty deps so the cleanup fires once, at unmount, and
  // only at unmount — every dismissal (Cancel, backdrop, hardware/gesture
  // back) reaches it because every dismissal tears this component down. The
  // refs are why [] is safe: a cleanup captured with [] sees the first
  // render's `query` and `onCommitTerm`, which would commit an empty string
  // through a possibly stale callback. Nothing here runs per keystroke.
  //
  // The host de-dupes, caps and drops empties — a whitespace-only term
  // commits nothing there, so this end does not need to test it.
  useEffect(() => () => { onCommitTermRef.current?.(queryRef.current); }, []);

  const viewed = recentViewed || [];
  const searched = recentSearched || [];
  const term = query.trim();
  const hasQuery = term !== '';
  const needle = term.toLowerCase();
  const columns = visibleColumns || [];

  // Matched ONCE per record per query, and the matched Set is carried along
  // rather than recomputed for the tint — 29 fields plus the child entries × N
  // records is cheap, but doing it twice for the same answer is not.
  //
  // ⚠️ MATCHING IS OVER SEARCHABLE_FIELDS; DISPLAY IS OVER visibleColumns.
  // The two are deliberately different lists. A rep who hid DIE/OP must still
  // find a tool by its die number — hiding a column tidies the grid, it does
  // not narrow the search. The consequence is visible and intended: a row can
  // come back with no tinted cell at all, because the field that matched is
  // one the rep chose not to show.
  const results = useMemo(() => {
    if (!hasQuery) return [];
    const out = [];
    for (const record of records || []) {
      const matched = matchedKeys(record, needle);
      if (matched.size > 0) out.push({ record, matched });
    }
    return out;
  }, [records, needle, hasQuery]);

  return (
    <div
      className="fso"
      data-testid="field-search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search records"
    >
      <div className="fso-header">
        <div className="fso-inputwrap">
          <span className="fso-inputicon" aria-hidden="true"><SearchIcon /></span>
          <input
            type="search"
            className="fso-input"
            data-testid="field-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search records"
            aria-label="Search records"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {/* Our own clear, not the platform's: the WebKit cancel button is
              hidden in CSS because it only appears on some engines, sits at a
              size iOS chooses, and gives no hook for a test id. Rendered only
              when there is something to clear — a permanently visible × on an
              empty field is a control that does nothing. */}
          {hasQuery && (
            <button
              type="button"
              className="fso-clear"
              data-testid="field-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {/* Dismiss ONLY. It must not also commit — the unmount cleanup above
            is the single commit site, and calling both would double-commit
            the term on the one dismissal that runs a handler. */}
        <button
          type="button"
          className="fso-cancel"
          data-testid="field-search-cancel"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>

      <div className="fso-body">
        {/* Non-empty query: recents give way to results, and the count bar is
            stated before the table so a rep knows the size of what they are
            about to scroll. */}
        {hasQuery && (
          <div className="fso-results" data-testid="field-search-results">
            <p className="fso-count" data-testid="field-search-count">
              {results.length} record{results.length === 1 ? '' : 's'} found
            </p>
            {results.length === 0 ? (
              <p className="fso-noresults" data-testid="field-search-noresults">
                No records match &quot;{term}&quot;
              </p>
            ) : (
              /* .frl-table because FieldRecordRow emits <tr>/<td> and needs a
                 real table ancestor — and because reusing the records screen's
                 own table styling is what makes a result row look like the row
                 it is. Its own scroller: the header stays put while a wide
                 result set travels in both axes, same as the grid. */
              <div className="fso-tablescroll">
                <table className="frl-table">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th key={col.label}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(({ record, matched }) => (
                      <FieldRecordRow
                        key={record.record_id}
                        record={record}
                        visibleColumns={columns}
                        dirtyIds={dirtyIds}
                        onRowTap={onRowTap}
                        /* B-254 D-9: the grid's tap, unpinned here — matching excludes bools,
                           so a tick never changes which rows match. */
                        onFlagTap={onFlagTap}
                        matched={matched}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {!hasQuery && (
          <>
            <section className="fso-section" data-testid="field-search-recent-viewed">
              <h2 className="fso-sectiontitle">Recently Viewed</h2>
              {viewed.length === 0 ? (
                <p className="fso-empty">No recently viewed records</p>
              ) : (
                viewed.map((item) => (
                  <button
                    key={item.record_id}
                    type="button"
                    className="fso-row"
                    data-testid={`field-search-viewed-${item.record_id}`}
                    onClick={() => onRecentTap(item)}
                  >
                    <span className="fso-rowicon" aria-hidden="true"><ClockIcon /></span>
                    <span className="fso-rowlabel">{item.label}</span>
                  </button>
                ))
              )}
            </section>

            <div className="fso-divider" role="separator" />

            <section className="fso-section" data-testid="field-search-recent-searched">
              <h2 className="fso-sectiontitle">Recently Searched</h2>
              {searched.length === 0 ? (
                <p className="fso-empty">No recent searches</p>
              ) : (
                searched.map((t) => (
                  /* Still a div, still NOT a button-inside-a-button: the row
                     holds two independent controls now. Phase 2 makes the body
                     live — tapping the term seeds the query, which re-runs the
                     search through the same state the input writes to, so
                     there is one search path and not a second one. */
                  <div
                    key={t}
                    className="fso-row fso-row-term"
                    data-testid={`field-search-term-${t}`}
                  >
                    <button
                      type="button"
                      className="fso-termbody"
                      data-testid={`field-search-term-run-${t}`}
                      onClick={() => setQuery(t)}
                    >
                      <span className="fso-rowicon" aria-hidden="true"><ClockIcon /></span>
                      <span className="fso-rowlabel">{t}</span>
                    </button>
                    <button
                      type="button"
                      className="fso-rowremove"
                      data-testid={`field-search-term-remove-${t}`}
                      onClick={() => onRemoveSearch(t)}
                      aria-label={`Remove ${t} from recent searches`}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
